import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { getClientActivity } from "../../api/adminService";
import { formatCurrency, formatDate, OrderStatusColors, OrderStatusLabels } from "./constants";
import { EmptyState, formatCount, sourceLabel } from "./analyticsShared";

const TABS = [
  { value: "entries", label: "Entered app", icon: LoginRoundedIcon },
  { value: "products", label: "Products viewed", icon: VisibilityRoundedIcon },
  { value: "categories", label: "Categories browsed", icon: CategoryRoundedIcon },
  { value: "stores", label: "Stores visited", icon: StorefrontRoundedIcon },
  { value: "wishlist", label: "Wishlist", icon: FavoriteRoundedIcon },
  { value: "purchases", label: "Purchased", icon: ShoppingBagRoundedIcon },
];

export default function ClientActivityModal({ clientId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("entries");

  useEffect(() => {
    if (!clientId) return;
    let active = true;
    setLoading(true);
    setError("");
    setTab("entries");
    getClientActivity(clientId)
      .then((result) => {
        if (active) setData(result);
      })
      .catch(() => {
        if (active) setError("Could not load this client's activity.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clientId]);

  const handleClose = () => {
    onClose();
    setData(null);
  };

  return (
    <Dialog open={Boolean(clientId)} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        {data ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: "primary.main" }}>{data.fullName?.[0]?.toUpperCase() || "?"}</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap>
                {data.fullName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                {data.email} {data.phone ? `· ${data.phone}` : ""}
              </Typography>
            </Box>
          </Stack>
        ) : (
          "Client activity"
        )}
        <IconButton onClick={handleClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <EmptyState text={error} />
          </Box>
        ) : data ? (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ px: 3, pt: 2 }}>
              <Chip size="small" variant="outlined" label={`Joined ${formatDate(data.createdAt)}`} />
              <Chip size="small" variant="outlined" label={`First seen ${formatDate(data.firstSeenAt)}`} />
              <Chip size="small" variant="outlined" label={`Last seen ${formatDate(data.lastSeenAt)}`} />
              <Chip size="small" variant="outlined" label={`${formatCount(data.appOpenCount)} app opens`} />
            </Stack>

            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2, mt: 1, minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600 } }}
            >
              {TABS.map((t) => (
                <Tab key={t.value} value={t.value} icon={<t.icon sx={{ fontSize: 18 }} />} iconPosition="start" label={t.label} />
              ))}
            </Tabs>

            <Box sx={{ p: 3, pt: 2 }}>
              {tab === "entries" && <EntriesList entries={data.recentEntries} />}
              {tab === "products" && <ProductViewsList items={data.recentProductViews} />}
              {tab === "categories" && <CategoryVisitsList items={data.recentCategoryVisits} />}
              {tab === "stores" && <StoreVisitsList items={data.recentStoreVisits} />}
              {tab === "wishlist" && <WishlistList items={data.wishlistItems} />}
              {tab === "purchases" && <PurchasesList items={data.purchasedProducts} />}
            </Box>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function Row({ avatar, primary, secondary, right }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
      {avatar}
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {primary}
        </Typography>
        {secondary && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            {secondary}
          </Typography>
        )}
      </Box>
      {right}
    </Stack>
  );
}

function ListWrap({ children, empty }) {
  const items = React.Children.toArray(children);
  if (items.length === 0) return <EmptyState text={empty} />;
  return (
    <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />} sx={{ maxHeight: 420, overflowY: "auto" }}>
      {children}
    </Stack>
  );
}

function EntriesList({ entries }) {
  return (
    <ListWrap empty="No app-entry events recorded for this client yet.">
      {(entries || []).map((e, i) => (
        <Row
          key={i}
          avatar={
            <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: "action.hover" }}>
              <LoginRoundedIcon fontSize="small" color="action" />
            </Avatar>
          }
          primary={sourceLabel(e.source) || "Unknown source"}
          secondary={e.latitude && e.longitude ? `${e.latitude.toFixed(3)}, ${e.longitude.toFixed(3)}` : undefined}
          right={
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {formatDate(e.createdAt)}
            </Typography>
          }
        />
      ))}
    </ListWrap>
  );
}

function ProductViewsList({ items }) {
  return (
    <ListWrap empty="This client hasn't viewed any product pages yet.">
      {(items || []).map((p, i) => (
        <Row
          key={i}
          avatar={
            <Avatar src={p.imageUrl || undefined} variant="rounded" sx={{ width: 36, height: 36 }}>
              <Inventory2RoundedIcon fontSize="small" />
            </Avatar>
          }
          primary={p.productName || "Deleted product"}
          secondary={p.source ? `From ${sourceLabel(p.source)}` : undefined}
          right={
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {formatDate(p.createdAt)}
            </Typography>
          }
        />
      ))}
    </ListWrap>
  );
}

function CategoryVisitsList({ items }) {
  return (
    <ListWrap empty="This client hasn't browsed any categories yet.">
      {(items || []).map((c, i) => (
        <Row
          key={i}
          avatar={
            <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: "action.hover" }}>
              <CategoryRoundedIcon fontSize="small" color="action" />
            </Avatar>
          }
          primary={c.categoryName || "Deleted category"}
          secondary={`Last visited ${formatDate(c.createdAt)}`}
          right={
            <Chip
              size="small"
              variant="outlined"
              label={`${formatCount(c.visitCount)} visit${c.visitCount === 1 ? "" : "s"}`}
              sx={{ flexShrink: 0 }}
            />
          }
        />
      ))}
    </ListWrap>
  );
}

function StoreVisitsList({ items }) {
  return (
    <ListWrap empty="This client hasn't visited any stores yet.">
      {(items || []).map((s, i) => (
        <Row
          key={i}
          avatar={
            <Avatar src={s.logoUrl || undefined} variant="rounded" sx={{ width: 36, height: 36 }}>
              <StorefrontRoundedIcon fontSize="small" />
            </Avatar>
          }
          primary={s.storeName || "Deleted store"}
          secondary={`Last visited ${formatDate(s.createdAt)}`}
          right={
            <Chip
              size="small"
              variant="outlined"
              label={`${formatCount(s.visitCount)} visit${s.visitCount === 1 ? "" : "s"}`}
              sx={{ flexShrink: 0 }}
            />
          }
        />
      ))}
    </ListWrap>
  );
}

function WishlistList({ items }) {
  return (
    <ListWrap empty="This client's wishlist is empty.">
      {(items || []).map((w) => (
        <Row
          key={w.productId}
          avatar={
            <Avatar src={w.imageUrl || undefined} variant="rounded" sx={{ width: 36, height: 36 }}>
              <FavoriteRoundedIcon fontSize="small" />
            </Avatar>
          }
          primary={w.productName}
          secondary={`Added ${formatDate(w.addedAt)}`}
          right={
            <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
              {formatCurrency(w.price)}
            </Typography>
          }
        />
      ))}
    </ListWrap>
  );
}

function PurchasesList({ items }) {
  return (
    <ListWrap empty="This client hasn't purchased anything yet.">
      {(items || []).map((o, i) => (
        <Row
          key={`${o.orderId}-${o.productId}-${i}`}
          avatar={
            <Avatar src={o.imageUrl || undefined} variant="rounded" sx={{ width: 36, height: 36 }}>
              <ShoppingBagRoundedIcon fontSize="small" />
            </Avatar>
          }
          primary={`${o.productName} ×${o.quantity}`}
          secondary={`${o.storeName} · ${o.orderNumber} · ${formatDate(o.createdAt)}`}
          right={
            <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
              <Typography variant="body2" fontWeight={700}>
                {formatCurrency(o.unitPrice * o.quantity)}
              </Typography>
              <Chip
                size="small"
                label={OrderStatusLabels[o.status] || "Unknown"}
                color={OrderStatusColors[o.status] || "default"}
                sx={{ height: 18, fontSize: 10 }}
              />
            </Stack>
          }
        />
      ))}
    </ListWrap>
  );
}
